import React from 'react';

import { Box, Flex } from '@opendoor/bricks/core';
import { centsToCurrencyString } from '@opendoor/shared-fe/build/formatUtils';
import { RefreshErrorModal } from 'sellDirect/components/offer/RefreshErrorModal';

import { getHubSellingRoute } from 'shared/helpers/routes';
import { Offer, useOfferContext } from 'sellDirect/OfferContext';

import { DataBlock, ISection, ModuleCard } from 'shared/components/overview/ModuleCard';
import { TasksSectionContent } from 'shared/components/TasksSectionContent';
import { SellerTasksAction } from 'sellDirect/components/overview/SellerTasksAction';
import { LocalDate } from '@js-joda/core';
import { MONTH_DAY_FORMAT } from 'shared/helpers/datetime';
import { useSellDirectTasks } from 'sellDirect/helpers/useSellDirectTasks';
import {
  AddressSelectionDropdown,
  AddressSelectionLeadProps,
} from 'platform/components/AddressSelectionDropdown';
import { useMarkOfferAsViewed } from 'platform/graphql/useMarkOfferAsViewed';

/**
 * The main header in the overview page
 * Provides information for the user that includes their prelim offer amount
 * current status and when the current offer expires
 * Tasks can be displayed here for a user if there is anything pending
 */
export const SellingModule: React.FC<
  {
    offer: Offer;
  } & AddressSelectionLeadProps
> = ({ offer, customerLeads, lead }) => {
  // Marks the offer viewed for customers that land in /overview
  useMarkOfferAsViewed();

  const { tasks } = useSellDirectTasks();
  const { closeRefreshErrorModal, isRefreshErrorModalOpen, refreshStatus } = useOfferContext();
  const PRICE_CONTENT = centsToCurrencyString(offer.headlinePriceCents);

  const { status, dateLabel, date } = ((): {
    status: string;
    dateLabel: string;
    date?: LocalDate;
  } => {
    if (offer.state === 'Closed') {
      return {
        status: 'Closed',
        dateLabel: 'Closing date',
        date: offer.closeEscrowDate,
      };
    } else if (offer.state === 'Withdrawn') {
      return {
        status: 'Withdrawn',
        dateLabel: 'Withdrawal Date',
        date: offer.withdrawnAt?.toLocalDate(),
      };
    } else if (offer.state === 'UnderContract') {
      return {
        status: 'Closing',
        dateLabel: 'Closing date',
        date: offer.closeEscrowDate,
      };
    } else if (offer.state === 'Finalized') {
      return {
        status: 'Finalized',
        dateLabel: 'Valid until',
        date: offer.expiresAt?.toLocalDate(),
      };
    } else if (offer.isExpired) {
      return {
        status: 'Expired',
        dateLabel: 'Expiration date',
        date: offer.expiresAt?.toLocalDate(),
      };
    }

    return {
      status: 'Preliminary',
      dateLabel: 'Valid until',
      date: offer.expiresAt?.toLocalDate(),
    };
  })();

  const priceSection: ISection = {
    content: (
      <Flex justifyContent={['space-between', null, 'flex-start']}>
        <DataBlock
          label="Offer"
          content={PRICE_CONTENT}
          to={getHubSellingRoute()}
          eventName="selling-module-price"
          mr={[5, null, 8, 10]}
          ariaLabel={`${PRICE_CONTENT} is the preliminary offer for your home. Navigate to the sell tab to help us finalize this offer.`}
        />
        <DataBlock label="Status" content={status} mr={[5, null, 8, 10]} />
        <DataBlock label={dateLabel} content={date ? date.format(MONTH_DAY_FORMAT) : ''} />
      </Flex>
    ),
  };

  const tasksSection: ISection = {
    title: 'To do',
    content: (
      <>
        <TasksSectionContent tasks={tasks ?? []} />
        <SellerTasksAction />
      </>
    ),
  };

  const showAddressDropdown = customerLeads.length > 1;
  return (
    <Box as="span" width="100%">
      {showAddressDropdown && (
        <AddressSelectionDropdown customerLeads={customerLeads} lead={lead} attachedToCard />
      )}
      <ModuleCard
        data-testid="selling-card"
        ariaLabel="Selling card module"
        pt={showAddressDropdown ? [7, null, 8] : 'intial'}
        px={[5, 7, 8]}
        sections={[priceSection, tasksSection]}
        borderRadius={['none', null, 'semiRounded']}
        title="Selling"
      />
      <RefreshErrorModal
        closeModal={closeRefreshErrorModal}
        isOpen={isRefreshErrorModalOpen}
        refreshStatus={refreshStatus}
      />
    </Box>
  );
};
